/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Visit } from '@/services/requests/housien-models/Visit';

/** The visit statuses that still ask something of the broker. */
const PENDING = 'pending';
const CONFIRMED = 'confirmed';

/**
 * What the attendance's visit asks for next.
 *
 * `schedule` is not a fallback for "we do not know": it is the answer
 * when nothing is booked, which includes an attendance whose only visit
 * already happened or fell through.
 */
export type VisitAction = 'schedule' | 'confirm' | 'complete';

export interface NextVisit {
  /** What the primary button should offer. */
  action: VisitAction;
  /** The visit that answer refers to; absent when the answer is `schedule`. */
  visit?: Visit;
}

/**
 * Whether a visit still stands, in the sense the funnel uses.
 *
 * `done` is deliberately absent. It counts as evidence the attendance
 * reached the visit stage -- which is why the stage stays put after one
 * happens -- but it asks nothing of the broker, so it is not what the
 * action bar should offer to act on.
 *
 * @param visit - The visit being judged.
 * @returns True while the visit is still ahead of the broker.
 */
function isOutstanding(visit: Visit): boolean {
  return visit.status === PENDING || visit.status === CONFIRMED;
}

/**
 * The attendance's visits, soonest first.
 *
 * Undated visits sort last rather than first: an unreadable date is the
 * one thing that must not win the "next visit" slot and put the action
 * bar on a record nobody can act on.
 *
 * @param visits - Every visit of the attendance.
 * @returns A new array, ordered.
 */
function bySoonest(visits: Visit[]): Visit[] {
  return [...visits].sort((a, b) => {
    const left = a.visit_datetime ?? '';
    const right = b.visit_datetime ?? '';
    if (!left) return 1;
    if (!right) return -1;
    return left < right ? -1 : 1;
  });
}

/**
 * What the action bar should offer, given the attendance's visits.
 *
 * The stage cannot answer this. `visit_scheduled` is derived from any
 * visit in `pending`, `confirmed` *or* `done`, so three attendances
 * wearing the same stage want three different actions -- confirm the
 * booking, record that it happened, or book another. The bar used to
 * offer "Agendar visita" to all three, which invited a second booking
 * on top of one waiting to be confirmed.
 *
 * The soonest outstanding visit wins. A broker with a confirmed visit
 * tomorrow and a pending one next month is asked about tomorrow's,
 * because that is the one the day turns on.
 *
 * @param visits - The attendance's visits, in any order.
 * @returns The action and the visit it refers to.
 */
export function nextVisitAction(visits: Visit[]): NextVisit {
  const outstanding = bySoonest(visits.filter(isOutstanding));
  const next = outstanding[0];
  if (!next) return { action: 'schedule' };
  return {
    action: next.status === PENDING ? 'confirm' : 'complete',
    visit: next,
  };
}

/** The status a given action moves its visit to. */
export const STATUS_AFTER: Record<Exclude<VisitAction, 'schedule'>, 'confirmed' | 'done'> = {
  confirm: 'confirmed',
  complete: 'done',
};
